<template>
  <v-card
    v-if="calculator"
    class="white vetalfa-view"
    :max-width="_getMaxWidth()"
  >
    <div class="vetalfa-header" ref="screenHeader">
      <vetalfa-small-header
        :title="category ? category.portugueseName : null"
        hasShareOption
      ></vetalfa-small-header>
      <vetalfa-drugs-header
        :isDrugsMode="true"
        :name="calculator.name"
        :description="calculator.classes[0]"
        :displayCalculator="false"
      ></vetalfa-drugs-header>
    </div>
    <div ref="screenContent">
      <div
        v-for="(variable, index) in calculator.variables"
        :key="`variable-${variable.id}`"
        v-bind:id="`variable-${variable.id}`"
      >
        <vetalfa-section-title
          :id="_createIdentifier(variable.name)"
          :prop="variable.name"
          :collapse="false"
        >
          <v-list v-if="variable.type == 'checklist'" class="ma-0 pa-0">
            <v-list-item-group
              @change="calculateResults(index, variables[variable.id])"
              v-model="variables[variable.id]"
            >
              <v-list-item
                v-for="(item, i) in variable.items"
                :key="item.description + i"
                v-slot="{ active }"
              >
                <v-list-item-icon class="mr-2">
                  <v-icon color="primary" v-if="active">{{
                    mdiCheckBoxOutline
                  }}</v-icon>
                  <v-icon v-else>{{ mdiCheckboxBlankOutline }}</v-icon>
                </v-list-item-icon>
                <v-row
                  class="ma-0 pa-0 pt-1"
                  style="font-size: 14px"
                  align="center"
                >
                  <v-col cols="11" class="ma-0 pa-0" align="start"
                    ><p class="ma-0 pa-0" style="word-wrap: normal">
                      {{ item.description }}
                    </p>
                  </v-col>
                  <v-col cols="1" class="ma-0 pa-0" align="end">{{
                    item.descriptionValue
                  }}</v-col>
                </v-row>
              </v-list-item>
            </v-list-item-group>
          </v-list>
          <animal-selector
            v-if="variable.type == 'select-animal'"
            v-on:selectAnimal="selectAnimal"
            :parentModelName="variable.id"
            :defaultValue="variable.default"
          >
          </animal-selector>
          <v-select
            v-if="variable.type == 'select'"
            :items="variable.items"
            :placeholder="variable.placeholder"
            hide-details
            filled
            class="mx-2 mb-5"
            dense
            :ref="`input-${variable.id}`"
            :clearable="variable.clearable"
            outlined
            v-model="variables[variable.id]"
            :value="variables[variable.id]"
            @change="changeSelect(variable)"
          ></v-select>
          <v-row class="ma-0 pa-0" v-if="variable.type == 'number'">
            <v-col cols="12" class="ma-0 pa-0"
              ><v-text-field
                v-bind:id="`numeric${variable.id}`"
                type="text"
                hide-details
                inputmode="decimal"
                :suffix="variable.suffix || ''"
                class="mx-2 my-0 mb-5"
                light
                dense
                color="primary"
                outlined
                :placeholder="variable.placeholder"
              ></v-text-field
            ></v-col>
          </v-row>
        </vetalfa-section-title>
      </div>
      <vetalfa-section-title
        v-if="calculator.responseLabel"
        :id="_createIdentifier(calculator.responseLabel)"
        :prop="calculator.responseLabel"
        :collapse="false"
      >
        <div v-if="response && response.type == 'array'">
          <vetalfa-section-item
            v-for="(resp, index) in response.values"
            :key="index + resp"
            :prop="resp"
            result
            :index="index"
          >
          </vetalfa-section-item>
        </div>
        <div v-else-if="response && response.type == 'error'">
          <vetalfa-section-paragraph
            :result="!!response"
            :prop="response.description"
            color="error"
          ></vetalfa-section-paragraph>
        </div>
        <div v-else-if="response && response.type == 'multiple'">
          <vetalfa-section-item-title
            v-for="(obj, index) in response.values"
            :key="index + obj.description"
            :prop="obj.description"
          >
            <vetalfa-section-item :prop="obj.value" result :index="0">
            </vetalfa-section-item>
          </vetalfa-section-item-title>
        </div>
        <div v-else-if="response && response.type == 'class'">
          <vetalfa-section-paragraph
            :result="!!response"
            :prop="response.value"
            :class="response.class"
          ></vetalfa-section-paragraph>
        </div>
        <vetalfa-section-paragraph
          v-else
          :result="!!response"
          :prop="response || calculatorLabels.RESPONSE_PLACEHOLDER"
        ></vetalfa-section-paragraph>
      </vetalfa-section-title>
      <vetalfa-general-tab
        :sections="collapsedSections"
        :references="calculator.references"
        :collapseAll="!calculator.showAllSections"
      ></vetalfa-general-tab>
    </div>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import calculatorLabels from "@/constants/labels/calculators.labels.js";
import animals from "@/constants/animals.enum.js";
import AnimalSelector from "@/components/AnimalSelector.vue";
import { mdiCheckboxBlankOutline, mdiCheckBoxOutline } from "@mdi/js";
import labels from "@/constants/labels.js";
export default {
  name: "Calculators",
  computed: {
    ...mapGetters("drugs", ["category"]),
    ...mapGetters("calculators", ["calculator", "calculatorParameters"]),
    collapsedSections() {
      let arr = [];
      this.calculator.sections.forEach((s) => {
        arr.push({ ...s, collapse: true });
      });
      return arr;
    },
  },
  components: {
    "animal-selector": AnimalSelector,
  },
  methods: {
    ...mapActions("general", ["routeRedirect"]),
    ...mapActions("calculators", ["openCalculatorById"]),
    ...mapMutations("calculators", ["setCalculatorParameters"]),
    getSectionNavagiationName(index, model) {
      if (model !== undefined) {
        if (this.calculator.variables.length == index + 1 && this.response) {
          return this.calculator.responseLabel;
        } else if (this.calculator.variables.length != index + 1) {
          return this.calculator.variables[index + 1].name;
        }
      }
      return null;
    },
    navigateToSection(section) {
      if (section) {
        setTimeout(
          function () {
            this.$vuetify.goTo("#" + this._createIdentifier(section), {
              duration: 500,
              offset: this.$refs.screenHeader.offsetHeight,
              easing: "easeInOutCubic",
            });
          }.bind(this),
          100
        );
      }
    },
    selectAnimal(obj) {
      this.variables[obj.id] = obj.value;
      let variable = this.calculator.variables.filter((f) => {
        return f.id == obj.id;
      })[0];
      this.changeSelect(variable);
    },
    changeSelect(variable) {
      if (variable.reset) {
        variable.reset.forEach((item) => {
          let defaultValue = this.calculator.variables.filter((f) => {
            return f.id == item;
          })[0].default;
          this.variables[item] = defaultValue;
          this.$refs[`input-${item}`][0].reset();
        });
      }
      if (variable.change) {
        variable.change(this.variables[variable.id]);
      }
      this.calculateResults();
    },
    calculateResults(section, model) {
      setTimeout(
        function () {
          if (this.calculator.type == "score") {
            let scoreVariables = {};
            this.calculator.variables.forEach((v) => {
              for (let key in this.variables) {
                if (v.id == key && typeof this.variables[key] == "number") {
                  scoreVariables[key] = v.items[this.variables[key]].value;
                }
              }
            });
            this.response = this.calculator.calculate(scoreVariables);
          } else {
            this.response = this.calculator.calculate(this.variables);
          }
          if (typeof section == "number") {
            this.navigateToSection(
              this.getSectionNavagiationName(section, model)
            );
          }
        }.bind(this),
        1
      );
    },
    parseNumberField(e) {
      const id = e.srcElement.id.replace("numeric", "");
      const { value } = this._formatNumberField(e, this.variables[id]);
      const variable = this.calculator.variables.filter((c) => {
        return c.id == id;
      })[0];
      if (variable.displayUnit && this.variables[variable.displayUnit]) {
        const suffix_id = `suffix${variable.displayUnit}`;
        let previousSuffix = document.getElementById(suffix_id);
        if (previousSuffix) {
          previousSuffix.remove();
        }
        let newSuffix = document.createElement("div");
        newSuffix.id = suffix_id;
        newSuffix.classList.add("v-text-field__suffix");
        newSuffix.innerHTML = `${this.variables[variable.displayUnit]}`;
        let refNode = document.getElementById(e.srcElement.id);
        refNode.parentNode.insertBefore(newSuffix, refNode.nextSibling);
      }
      this.variables[id] = value;
      this.calculateResults();
    },
    setupCalculator() {
      this._setupScreenSizes(this);
      if (this.calculator && this.hasMounted) {
        this.calculator.variables.forEach((v) => {
          this.variables[v.id] = v.default;
        });
        this.calculator.variables.forEach((v) => {
          if (v.type == "number") {
            let el = document.getElementById(`numeric${v.id}`);
            el.value = 0;
            el.addEventListener("input", this.parseNumberField);
            el.addEventListener("focus", this.parseNumberField);
            el.addEventListener("blur", this.parseNumberField);
          }
        });
        if (!!this.calculatorParameters) {
          this.sufixes = this.calculatorParameters.sufixes;
          this.variables = this.calculatorParameters.variables;
          this.response = this.calculatorParameters.response;
        }
        if (this.calculator.mounted) {
          this.calculator.mounted();
        }
      }
    },
  },
  data() {
    return {
      hasMounted: false,
      calculatorLabels,
      response: null,
      variables: {},
      sufixes: [],
      animals,
      mdiCheckboxBlankOutline,
      mdiCheckBoxOutline,
      numericRules: [
        (value) =>
          !value || new Number(value) >= 0 || calculatorLabels.NUMBER_RULE,
      ],
      labels,
    };
  },
  watch: {
    response(val) {
      this.setCalculatorParameters({
        response: val,
        variables: this.variables,
        sufixes: this.sufixes,
      });
    },
    variables(val) {
      this.setCalculatorParameters({
        response: this.response,
        variables: val,
        sufixes: this.sufixes,
      });
    },
    sufixes(val) {
      this.setCalculatorParameters({
        response: this.response,
        variables: this.variables,
        sufixes: val,
      });
    },
  },
  mounted() {
    if (!this.calculator || this.calculator.id !== this.$route.params.id) {
      this.openCalculatorById({
        id: this.$route.params.id,
        redirect: false,
      }).then(() => {
        this.hasMounted = true;
        this.setupCalculator();
      });
    } else {
      this.hasMounted = true;
      this.setupCalculator();
    }
  },
};
</script>
